/**
 * Box annotation geometry properties — registration and getters.
 *
 * Properties registered here:
 * - box_position (COORDINATE, PROJECT_GLOBAL) — {x, y, z}
 * - box_scale (COORDINATE, PROJECT_GLOBAL) — {x, y, z}
 * - box_rotation (COORDINATE, PROJECT_GLOBAL) — {x, y, z}
 */

import { PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';

// ============================================================================
// PROPERTY REGISTRATION
// ============================================================================

export function registerBoxGeometryProperties() {
  if (!propertyRegistry.isRegistered('box_position')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'box_position',
      name: 'Position',
      valueType: PropertyValueType.COORDINATE,
      defaultValue: { x: 0, y: 0, z: 0 },
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('box_scale')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'box_scale',
      name: 'Scale',
      valueType: PropertyValueType.COORDINATE,
      defaultValue: { x: 1, y: 1, z: 1 },
      annotationTypes: [],
    });
  }

  if (!propertyRegistry.isRegistered('box_rotation')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'box_rotation',
      name: 'Rotation',
      valueType: PropertyValueType.COORDINATE,
      defaultValue: { x: 0, y: 0, z: 0 },
      annotationTypes: [],
    });
  }
}

// Register immediately on import
registerBoxGeometryProperties();

// ============================================================================
// GETTERS (uses global property service)
// ============================================================================

type Coordinate = { x: number; y: number; z: number };

export function getBoxPositionProperty(annotationId: string): ReactiveProperty<Coordinate> {
  const service = getGlobalPropertyService();
  return service.getProperty('box_position', annotationId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
}

export function getBoxScaleProperty(annotationId: string): ReactiveProperty<Coordinate> {
  const service = getGlobalPropertyService();
  return service.getProperty('box_scale', annotationId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
}

export function getBoxRotationProperty(annotationId: string): ReactiveProperty<Coordinate> {
  const service = getGlobalPropertyService();
  return service.getProperty('box_rotation', annotationId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
}
